import { categorizeArtifactChange, UPDATED_COMPONENTS_CATEGORY } from "../shared/mcp-chat-change-category";
import type { DiffActivityGroup, DiffTreeNode, StructuralDiff, ToolParameterObject } from "../shared/mcp-chat-types";

// ── Snapshot data model ──

interface SnapshotEntry {
  componentType: string;
  friendlyName: string | null;
  parentId: number | null;
}

export type DictSnapshot = Map<number, SnapshotEntry>;

// ── Snapshot capture ──

export function captureSnapshot(): DictSnapshot | null {
  try {
    const sd = (window as any).getSdInstance?.();
    const dict = sd?.screenProvider?.state?.screenDesignDictionary;
    if (!dict) return null;

    const snapshot: DictSnapshot = new Map();
    for (const key in dict) {
      const numKey = parseInt(key, 10);
      if (isNaN(numKey)) continue;

      const di = dict[numKey];
      if (!di || !di.item) continue;

      snapshot.set(numKey, {
        componentType: di.item.name || "Unknown",
        friendlyName: di.friendlyName || null,
        parentId: di.parentId ?? null
      });
    }

    return snapshot;
  } catch {
    return null;
  }
}

// ── Live dictionary shape (minimal — only what's needed for child ordering) ──

interface LiveDictChildRef {
  id?: number | string;
}

interface LiveDictNode {
  children?: (LiveDictChildRef | number | string)[];
}

type LiveDict = Record<number, LiveDictNode>;

interface ScreenProviderState {
  screenDesignDictionary?: LiveDict;
}

interface ScreenProviderShape {
  state?: ScreenProviderState;
}

interface SdInstanceShape {
  screenProvider?: ScreenProviderShape;
}

interface WindowWithSd {
  getSdInstance?: () => SdInstanceShape | undefined;
}

function getLiveDict(): LiveDict | null {
  try {
    const getSd = (window as unknown as WindowWithSd).getSdInstance;
    const sd = getSd?.();
    return sd?.screenProvider?.state?.screenDesignDictionary ?? null;
  } catch {
    return null;
  }
}

// Pure structural wrappers — hidden from the "Components" list so only
// meaningful, named/leaf components are shown (user decision: "sadece anlamlı
// componentler"). Verified against live di.item.name values.
const STRUCTURAL_WRAPPER_TYPES: ReadonlySet<string> = new Set(["Row", "Col", "Container", "Grid"]);

// ── Component diff (flat list of meaningful added components, in canvas order) ──

export interface ComponentDiffResult {
  tree: DiffTreeNode[];
  totalAdded: number;
}

export function computeComponentTree(before: DictSnapshot, after: DictSnapshot): ComponentDiffResult {
  const addedIds = new Set<number>();
  for (const id of after.keys()) {
    if (!before.has(id)) addedIds.add(id);
  }

  if (addedIds.size === 0) return { tree: [], totalAdded: 0 };

  const result: DiffTreeNode[] = [];
  const seen = new Set<number>();

  const pushMeaningful = (id: number): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const entry = after.get(id);
    if (!entry) return;
    if (STRUCTURAL_WRAPPER_TYPES.has(entry.componentType)) return;
    result.push({
      kuikaId: id,
      componentType: entry.componentType,
      displayName: entry.friendlyName || entry.componentType,
      children: []
    });
  };

  const dict = getLiveDict();

  if (dict) {
    // Depth-first from added roots, following di.children order, so the flat
    // list reads top-to-bottom like the canvas. Structural wrappers are walked
    // through (to reach their meaningful children) but never emitted.
    const visit = (id: number): void => {
      if (!addedIds.has(id)) return;
      pushMeaningful(id);
      const node = dict[id];
      if (!node?.children?.length) return;
      for (const child of node.children) {
        const rawId = typeof child === "object" && child !== null ? child.id : child;
        const childId = typeof rawId === "number" ? rawId : parseInt(String(rawId), 10);
        if (!isNaN(childId)) visit(childId);
      }
    };

    for (const id of addedIds) {
      const entry = after.get(id)!;
      const parentId = entry.parentId;
      const isRoot = parentId == null || !addedIds.has(parentId);
      if (isRoot) visit(id);
    }
    // Defensive: emit any meaningful added node not reached via DFS.
    for (const id of addedIds) pushMeaningful(id);
  } else {
    for (const id of addedIds) pushMeaningful(id);
  }

  return { tree: result, totalAdded: result.length };
}

// ── Activity grouping ──

interface ActivityInput {
  toolName: string;
  description: string;
  isMutating: boolean;
  status: string;
  input?: ToolParameterObject;
  /** Set when this activity is a synthesized specialist run (Designer/Wirer/Binder/…). */
  specialistAgent?: string;
}

// Real component kuikaIds are numeric; build-time markup refs (e.g. "@_c4") are
// synthetic. A synthetic target means the op acts on a just-rendered component
// that's already shown under "Added components".
function isSyntheticComponentId(id: string | number): boolean {
  return !/^-?\d+$/.test(String(id));
}

// Collects component target ids from a ui_component activity's input across the
// shapes the operations use (kuikaId, kuikaIds[], components[].kuikaId).
function collectComponentTargetIds(input: ToolParameterObject | undefined): string[] {
  if (!input) return [];
  const ids: string[] = [];
  const push = (value: unknown): void => {
    if (typeof value === "string" || typeof value === "number") ids.push(String(value));
  };
  push(input.kuikaId);
  const kuikaIds = input.kuikaIds;
  if (Array.isArray(kuikaIds)) for (const value of kuikaIds) push(value);
  const components = input.components;
  if (Array.isArray(components)) {
    for (const component of components) {
      if (component !== null && typeof component === "object" && !Array.isArray(component)) push(component.kuikaId);
    }
  }
  return ids;
}

// True when every component this op targets is synthetic — i.e. a build-time
// edit on a just-added component. Such ops are hidden from "Updated components"
// so it never duplicates "Added components" (no target ids → not filtered).
function targetsOnlySyntheticComponents(input: ToolParameterObject | undefined): boolean {
  const ids = collectComponentTargetIds(input);
  if (ids.length === 0) return false;
  return ids.every(isSyntheticComponentId);
}

export function buildActivityGroups(activities: ActivityInput[]): DiffActivityGroup[] {
  const groups = new Map<string, string[]>();

  for (const a of activities) {
    if (a.status !== "completed") continue;

    const operation = typeof a.input?.operation === "string" ? a.input.operation : undefined;
    const isSpecialist = !!a.specialistAgent || a.toolName.startsWith("specialist:");
    const category = categorizeArtifactChange({
      toolName: a.toolName,
      operation,
      isMutating: a.isMutating,
      isSpecialist
    });
    if (!category) continue;

    // "Updated components" must not echo components added this turn — those
    // build-time configures carry synthetic markup ids.
    if (category === UPDATED_COMPONENTS_CATEGORY && targetsOnlySyntheticComponents(a.input)) continue;

    if (!groups.has(category)) groups.set(category, []);
    groups.get(category)!.push(a.description);
  }

  const result: DiffActivityGroup[] = [];
  for (const [category, items] of groups) {
    result.push({ category, items });
  }
  return result;
}

// ── Build full structural diff ──

export function buildStructuralDiff(
  before: DictSnapshot | null,
  after: DictSnapshot | null,
  activities: ActivityInput[]
): StructuralDiff | undefined {
  const componentResult = before && after ? computeComponentTree(before, after) : { tree: [], totalAdded: 0 };

  const activityGroups = buildActivityGroups(activities);
  const totalActivities = activityGroups.reduce((s, g) => s + g.items.length, 0);

  if (componentResult.tree.length === 0 && activityGroups.length === 0) {
    return undefined;
  }

  return {
    tree: componentResult.tree,
    totalAdded: componentResult.totalAdded,
    activityGroups,
    totalChanges: componentResult.totalAdded + totalActivities
  };
}
